// Add this at the top of cvss.js
// Use a resilient import that works for both CJS and ESM builds of the library
import * as CVSSModule from 'cvss'; // Ensure dependency exists in package.json
// Normalize potential shapes: default export for CJS interop or named/object export
const CVSS_ANY = (CVSSModule && (CVSSModule.default ?? CVSSModule)) || null;

// Metric labels and descriptions for CVSS v3.x
const metricLabels = {
  AV: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' },
  AC: { L: 'Low', H: 'High' },
  PR: { N: 'None', L: 'Low', H: 'High' },
  UI: { N: 'None', R: 'Required' },
  S: { U: 'Unchanged', C: 'Changed' },
  C: { N: 'None', L: 'Low', H: 'High' },
  I: { N: 'None', L: 'Low', H: 'High' },
  A: { N: 'None', L: 'Low', H: 'High' },
};

const metricDescriptions = {
  AV: {
    N: 'Remotely exploitable (Network)',
    A: 'Attack requires access to the local network (Adjacent)',
    L: 'Attack requires local access (Local)',
    P: 'Attack requires physical access (Physical)',
  },
  AC: {
    L: 'Low complexity (easy to exploit)',
    H: 'High complexity (harder to exploit)',
  },
  PR: {
    N: 'No privileges required',
    L: 'Low privileges required',
    H: 'High privileges required',
  },
  UI: {
    N: 'No user interaction required',
    R: 'User interaction required',
  },
  S: {
    U: 'Scope unchanged (affects only vulnerable component)',
    C: 'Scope changed (can affect other components)',
  },
  C: {
    N: 'No confidentiality impact',
    L: 'Low confidentiality impact',
    H: 'High confidentiality impact',
  },
  I: {
    N: 'No integrity impact',
    L: 'Low integrity impact',
    H: 'High integrity impact',
  },
  A: {
    N: 'No availability impact',
    L: 'Low availability impact',
    H: 'High availability impact',
  },
};

function roundUp(value) {
  return Math.ceil(value * 10) / 10;
}

// Warn only once to avoid console spam when the external library isn't usable
let _cvssLibWarnedOnce = false;
function warnOnce(msg, err) {
  if (_cvssLibWarnedOnce) return;
  _cvssLibWarnedOnce = true;
  // Use warn to reduce noise severity
  if (err) console.warn(msg, err);
  else console.warn(msg);
}

function calculateImpact(metrics) {
  const c = metrics.C === 'H' ? 0.56 : metrics.C === 'L' ? 0.22 : 0;
  const i = metrics.I === 'H' ? 0.56 : metrics.I === 'L' ? 0.22 : 0;
  const a = metrics.A === 'H' ? 0.56 : metrics.A === 'L' ? 0.22 : 0;

  const impactBase = 1 - ((1 - c) * (1 - i) * (1 - a));

  if (metrics.S === 'U') {
    return 6.42 * impactBase;
  } else {
    return 7.52 * (impactBase - 0.029) - 3.25 * Math.pow(impactBase * 0.9731 - 0.02, 13);
  }
}

function calculateExploitability(metrics) {
  const av = metrics.AV === 'N' ? 0.85 : metrics.AV === 'A' ? 0.62 : metrics.AV === 'L' ? 0.55 : 0.2;
  const ac = metrics.AC === 'L' ? 0.77 : 0.44;
  
  let pr;
  if (metrics.S === 'U') {
    pr = metrics.PR === 'N' ? 0.85 : metrics.PR === 'L' ? 0.62 : 0.27;
  } else {
    pr = metrics.PR === 'N' ? 0.85 : metrics.PR === 'L' ? 0.68 : 0.5;
  }
  
  const ui = metrics.UI === 'N' ? 0.85 : 0.62;
  
  return 8.22 * av * ac * pr * ui;
}

export function parseCVSSVector(vector) {
  if (!vector) return null;

  try {
    // Extract version and metrics
    const versionMatch = vector.match(/CVSS:([\d.]+)\//);
    if (!versionMatch) return null;

    const metrics = {};
    const metricPairs = vector.split('/').slice(1); // Skip the version part

    metricPairs.forEach(pair => {
      const [key, value] = pair.split(':');
      if (key && value && metricLabels[key]) {
        metrics[key] = value;
      }
    });

    // Validate we have all required metrics
    const requiredMetrics = ['AV', 'AC', 'PR', 'UI', 'S', 'C', 'I', 'A'];
    const hasAllMetrics = requiredMetrics.every(m => metrics[m] !== undefined);
    if (!hasAllMetrics) return null;

    // Calculate scores
    const impact = calculateImpact(metrics);
    const exploitability = calculateExploitability(metrics);

    let baseScore;
    if (impact <= 0) {
      baseScore = 0;
    } else {
      baseScore = impact + exploitability;
      if (metrics.S === 'C') {
        baseScore = 1.08 * (impact + exploitability);
      }
    }

    baseScore = roundUp(Math.min(10, baseScore));

    // Determine severity
    let severity = 'NONE';
    if (baseScore >= 9.0) severity = 'CRITICAL';
    else if (baseScore >= 7.0) severity = 'HIGH';
    else if (baseScore >= 4.0) severity = 'MEDIUM';
    else if (baseScore > 0) severity = 'LOW';

    return {
      baseScore,
      severity,
      metrics,
      vector,
      version: versionMatch[1]
    };
  } catch (e) {
    console.error('CVSS parsing failed:', e);
    return null;
  }
}

export function explainMetrics(metrics) {
  if (!metrics) return [];
  return Object.entries(metrics).map(([key, value]) => ({
    key,
    value,
    label: metricLabels[key]?.[value] || value,
    description: metricDescriptions[key]?.[value] || '',
  }));
}

// Helper function to round up to one decimal place
function roundUpToTenths(value) {
  return Math.ceil(value * 10) / 10;
}

// Improved fallback CVSS parsing
function parseCVSSFallback(vector) {
  if (!vector) return null;

  try {
    // Extract version and metrics
    const versionMatch = vector.match(/CVSS:([\d.]+)\//);
    if (!versionMatch) return null;

    const metrics = {};
    const metricPairs = vector.split('/').slice(1); // Skip the version part

    metricPairs.forEach(pair => {
      const [key, value] = pair.split(':');
      if (key && value && metricLabels[key]) {
        metrics[key] = value;
      }
    });

    // Validate we have all required metrics
    const requiredMetrics = ['AV', 'AC', 'PR', 'UI', 'S', 'C', 'I', 'A'];
    const hasAllMetrics = requiredMetrics.every(m => metrics[m] !== undefined);
    if (!hasAllMetrics) return null;

    // Calculate Base Score using the same logic as parseCVSSVector for consistency
    const impact = calculateImpact(metrics);
    const exploitability = calculateExploitability(metrics);

    let baseScore;
    if (impact <= 0) {
      baseScore = 0;
    } else {
      baseScore = impact + exploitability;
      if (metrics.S === 'C') {
        baseScore = 1.08 * (impact + exploitability);
      }
    }
    baseScore = roundUp(Math.min(10, baseScore));

    // Determine severity mapping identical to parseCVSSVector
    let severity = 'NONE';
    if (baseScore >= 9.0) severity = 'CRITICAL';
    else if (baseScore >= 7.0) severity = 'HIGH';
    else if (baseScore >= 4.0) severity = 'MEDIUM';
    else if (baseScore > 0) severity = 'LOW';

    return {
      baseScore,
      severity,
      metrics,
      vector,
    };
  } catch (e) {
    console.error('Fallback CVSS parsing failed:', e);
    return null;
  }
}


// Main CVSS parsing function
export function parseCVSSv3(vector) {
  if (!vector) return null;

  try {
    // First try with the external CVSS library in the same manner as before: callable instance only
    if (typeof CVSS_ANY === 'function') {
      let instance = null;
      try {
        instance = CVSS_ANY(vector);
      } catch {
        try { instance = new CVSS_ANY(vector); } catch { /* ignore */ }
      }

      if (instance && typeof instance.getBaseScore === 'function' && typeof instance.getBaseSeverity === 'function') {
        const baseScore = instance.getBaseScore();
        const severity = instance.getBaseSeverity();
        const metrics = typeof instance.getMetrics === 'function' ? instance.getMetrics() : (parseCVSSVector(vector)?.metrics || {});
        return { baseScore, severity, metrics, vector };
      }
    }

    // If the library isn't callable in the expected way, fall back to our internal implementation
    throw new Error('CVSS library not usable');
  } catch (e) {
    warnOnce('CVSS library parsing failed, using fallback:', e);
    return parseCVSSFallback(vector);
  }
}

// ... (keep the rest of your existing functions)
// Function to explain CVSS metrics
export function explainCVSSMetrics(metrics) {
  if (!metrics) return [];
  return Object.entries(metrics).map(([key, value]) => ({
    key,
    value,
    label: metricLabels[key]?.[value] || value,
    description: metricDescriptions[key]?.[value] || '',
  }));
}

// --- Minimal support for CVSS v4.0 and v2.0 metric label explanations ---
const metricLabelsV4 = {
  // Base Exploitability
  AV: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' },
  AC: { L: 'Low', H: 'High' },
  AT: { N: 'None', P: 'Present' }, // Attack Requirements (approx)
  PR: { N: 'None', L: 'Low', H: 'High' },
  UI: { N: 'None', R: 'Required', P: 'Passive' },
  // Base Impact (Vulnerable System)
  VC: { N: 'None', L: 'Low', H: 'High' },
  VI: { N: 'None', L: 'Low', H: 'High' },
  VA: { N: 'None', L: 'Low', H: 'High' },
  // Base Impact (Subsequent System)
  SC: { N: 'None', L: 'Low', H: 'High' },
  SI: { N: 'None', L: 'Low', H: 'High' },
  SA: { N: 'None', L: 'Low', H: 'High' },
};

const metricLabelsV2 = {
  AV: { N: 'Network', A: 'Adjacent', L: 'Local' },
  AC: { H: 'High', M: 'Medium', L: 'Low' },
  Au: { N: 'None', S: 'Single', M: 'Multiple' },
  C: { N: 'None', P: 'Partial', C: 'Complete' },
  I: { N: 'None', P: 'Partial', C: 'Complete' },
  A: { N: 'None', P: 'Partial', C: 'Complete' },
};

// Titles (friendly names) for metric keys by version
const metricTitlesV3 = {
  AV: 'Attack Vector',
  AC: 'Attack Complexity',
  PR: 'Privileges Required',
  UI: 'User Interaction',
  S: 'Scope',
  C: 'Confidentiality',
  I: 'Integrity',
  A: 'Availability',
};

const metricTitlesV4 = {
  AV: 'Attack Vector',
  AC: 'Attack Complexity',
  AT: 'Attack Requirements',
  PR: 'Privileges Required',
  UI: 'User Interaction',
  VC: 'Vulnerable System Confidentiality',
  VI: 'Vulnerable System Integrity',
  VA: 'Vulnerable System Availability',
  SC: 'Subsequent System Confidentiality',
  SI: 'Subsequent System Integrity',
  SA: 'Subsequent System Availability',
};

const metricTitlesV2 = {
  AV: 'Access Vector',
  AC: 'Access Complexity',
  Au: 'Authentication',
  C: 'Confidentiality Impact',
  I: 'Integrity Impact',
  A: 'Availability Impact',
};

// Minimal descriptions for v4 and v2 to improve the UI parity with v3
const metricDescriptionsV4 = {
  AV: { N: 'Network-reachable attack path', A: 'Adjacent network required', L: 'Local access required', P: 'Physical access required' },
  AC: { L: 'Few/no specialized conditions', H: 'Additional conditions required' },
  AT: { N: 'No additional attack requirements', P: 'Specific preconditions required' },
  PR: { N: 'No privileges required', L: 'Low privileges required', H: 'High privileges required' },
  UI: { N: 'No user interaction required', R: 'User interaction required', P: 'Passive user impact' },
  VC: { N: 'No confidentiality impact (vulnerable system)', L: 'Low confidentiality impact (vulnerable system)', H: 'High confidentiality impact (vulnerable system)' },
  VI: { N: 'No integrity impact (vulnerable system)', L: 'Low integrity impact (vulnerable system)', H: 'High integrity impact (vulnerable system)' },
  VA: { N: 'No availability impact (vulnerable system)', L: 'Low availability impact (vulnerable system)', H: 'High availability impact (vulnerable system)' },
  SC: { N: 'No confidentiality impact (subsequent system)', L: 'Low confidentiality impact (subsequent system)', H: 'High confidentiality impact (subsequent system)' },
  SI: { N: 'No integrity impact (subsequent system)', L: 'Low integrity impact (subsequent system)', H: 'High integrity impact (subsequent system)' },
  SA: { N: 'No availability impact (subsequent system)', L: 'Low availability impact (subsequent system)', H: 'High availability impact (subsequent system)' },
};

const metricDescriptionsV2 = {
  AV: { N: 'Network-accessible', A: 'Adjacent network access required', L: 'Local access required' },
  AC: { H: 'High complexity', M: 'Medium complexity', L: 'Low complexity' },
  Au: { N: 'No authentication needed', S: 'Single authentication', M: 'Multiple authentications' },
  C: { N: 'No confidentiality impact', P: 'Partial confidentiality impact', C: 'Complete confidentiality impact' },
  I: { N: 'No integrity impact', P: 'Partial integrity impact', C: 'Complete integrity impact' },
  A: { N: 'No availability impact', P: 'Partial availability impact', C: 'Complete availability impact' },
};

export function explainCVSSMetricsAny(metrics, version) {
  if (!metrics) return [];
  const major = typeof version === 'string' ? version.split('.')[0] : '';
  const labels = major === '4' ? metricLabelsV4 : (major === '2' ? metricLabelsV2 : metricLabels);
  const desc = (major === '4' ? metricDescriptionsV4 : (major === '2' ? metricDescriptionsV2 : metricDescriptions));
  const titles = (major === '4' ? metricTitlesV4 : (major === '2' ? metricTitlesV2 : metricTitlesV3));

  const groupFor = (k) => {
    if (major === '4') {
      if (['VC','VI','VA','SC','SI','SA'].includes(k)) return 'impact';
      return 'exploitability';
    }
    if (major === '2') {
      if (['C','I','A'].includes(k)) return 'impact';
      return 'exploitability';
    }
    // v3.x
    if (['C','I','A'].includes(k)) return 'impact';
    if (k === 'S') return 'scope';
    return 'exploitability';
  };

  return Object.entries(metrics).map(([key, value]) => ({
    key,
    value,
    label: labels[key]?.[value] || value,
    description: (desc[key]?.[value]) || '',
    title: titles[key] || key,
    group: groupFor(key),
  }));
}

// Lightweight generic vector parsing
export function parseCVSSVectorGeneric(vector) {
  if (!vector) return null;
  const m = vector.match(/CVSS:([0-9.]+)/);
  if (!m) return null;
  const version = m[1];
  const metrics = {};
  try {
    const parts = vector.split('/').slice(1);
    parts.forEach((pair) => {
      const [k, v] = pair.split(':');
      if (k && v) metrics[k] = v;
    });
    return { version, vector, metrics };
  } catch {
    return { version, vector, metrics };
  }
}

export function parseCVSSv4(vector) {
  const g = parseCVSSVectorGeneric(vector);
  if (!g) return null;
  return { ...g };
}

export function parseCVSSv2(vector) {
  const g = parseCVSSVectorGeneric(vector);
  if (!g) return null;
  return { ...g };
}

// Function to show detailed CVSS calculation breakdown
export function showCVSSCalculation(vector) {
  if (!vector) return null;

  console.log('=== CVSS v3.1 Calculation Breakdown ===');
  console.log('Vector:', vector);

  const metrics = {};
  const parts = vector.split('/');

  parts.forEach(part => {
    if (part.includes(':')) {
      const [key, value] = part.split(':');
      if (metricLabels[key]) {
        metrics[key] = value;
      }
    }
  });


  // --- Impact calculation ---
  const c = metrics.C === 'H' ? 0.56 : metrics.C === 'L' ? 0.22 : 0;
  const i = metrics.I === 'H' ? 0.56 : metrics.I === 'L' ? 0.22 : 0;
  const a = metrics.A === 'H' ? 0.56 : metrics.A === 'L' ? 0.22 : 0;
  const impactBase = 1 - ((1 - c) * (1 - i) * (1 - a));
  let impact = impactBase;

  if (metrics.S === 'C') {
    impact = 6.42 * (impactBase - 0.025);
    if (impact < 0) impact = 0;
  }

  // --- Exploitability calculation ---
  const av = metrics.AV === 'N' ? 0.85 : metrics.AV === 'A' ? 0.62 : metrics.AV === 'L' ? 0.55 : 0.2;
  const ac = metrics.AC === 'L' ? 0.77 : 0.44;
  const pr = metrics.PR === 'N' ? 0.85 : metrics.PR === 'L' ? 0.62 : 0.27;
  const ui = metrics.UI === 'N' ? 0.85 : 0.62;
  const exploitability = 8.22 * av * ac * pr * ui;


  // --- Base Score calculation ---
  let baseScore;
  if (impact <= 0) {
    baseScore = 0;
  } else if (metrics.S === 'U') {
    baseScore = impact + exploitability;
  } else {
    baseScore = 1.08 * (impact + exploitability);
  }

  baseScore = roundUpToTenths(Math.min(10, baseScore));


  return { baseScore, severity: getSeverity(baseScore), metrics };
}

// Helper function to determine severity
function getSeverity(score) {
  if (score >= 9.0) return 'CRITICAL';
  if (score >= 7.0) return 'HIGH';
  if (score >= 4.0) return 'MEDIUM';
  if (score > 0) return 'LOW';
  return 'NONE';
}